/**
 * ManifestRefusal.tsx — what to say when the Engine's manifest cannot be read.
 *
 * `capabilityManifest.ts` refuses a manifest for three distinct reasons, and
 * they have three distinct remedies: upgrade the client, fix a broken contract,
 * or find out why the bytes changed. Collapsing them into "could not load" would
 * leave an engineer with a workbench that shows nothing and a reason they must
 * go and discover themselves.
 *
 * There is deliberately NO RETRY. Every refusal in the runtime is terminal by
 * construction — retrying an incompatible contract produces the same refusal,
 * and a retry button invites exactly the wrong conclusion, that this is
 * probably just flaky.
 */

import type { CapabilityManifestError } from "../../api/capabilityManifest";

interface Remedy {
  readonly matches: (code: string) => boolean;
  readonly title: string;
  readonly body: string;
}

/**
 * The remedies, keyed by the refusal codes the runtime defines.
 *
 * The CODES are the runtime's own, and each one is matched exactly — a refusal
 * this component does not recognise renders with no remedy claim at all, rather
 * than borrowing one that might be wrong. Inventing a remedy for an unknown
 * failure is how a client starts explaining things it does not understand.
 */
const REMEDIES: readonly Remedy[] = [
  {
    matches: code => code === "engine-capability-manifest-version-unsupported",
    title: "This workbench and this Engine speak different capability contracts",
    body: "Neither is wrong. The workbench will not interpret a contract it was not written for, because a client that half-understands a contract draws wrong pictures of real plants. Upgrade whichever side is behind.",
  },
  {
    matches: code => code === "engine-capability-manifest-digest-mismatch",
    title: "The Engine's capabilities changed under a pinned digest",
    body: "This deployment pinned the exact manifest content it qualified against, and the Engine has since published something different. Re-qualify the deployment against the new manifest before editing anything.",
  },
  {
    matches: code => code === "engine-capability-manifest-incomplete",
    title: "The Engine published an incomplete capability manifest",
    body: "A section this workbench needs is missing. Reporting the capabilities that did arrive would be indistinguishable from reporting them as unsupported, so nothing is shown instead.",
  },
  {
    matches: code => code === "engine-capability-manifest-malformed",
    title: "The Engine published a capability manifest this workbench cannot read",
    body: "A required descriptor field is absent or holds a value outside the published vocabulary. Nothing is rendered rather than something partly guessed.",
  },
];

export function ManifestRefusal({ error }: { error: CapabilityManifestError }): JSX.Element {
  const remedy = REMEDIES.find(entry => entry.matches(error.code));
  return (
    <div className="banner banner-error" role="alert">
      <div>
        <strong>{remedy?.title ?? "The Engine's capability manifest could not be read"}</strong>
        <p>{error.message}</p>
        {remedy && <p className="muted">{remedy.body}</p>}
        {error.status !== undefined && <p className="muted">HTTP {error.status}</p>}
        <p className="request-id">{error.code}</p>
      </div>
    </div>
  );
}
